import { useDispatch, useSelector } from "react-redux";

import ProductImage from "../common/components/Product/ProductImage";
import { Icon } from "../common/components/icon/Icon";
import { IconButton } from "@mui/material";

import { IconType } from "../common/components/icon/IconType";
import { Price, PriceType, RebateLabelType } from "../common/components/Price/v1/Price";
import { SimpleStockIndicator } from "../common/components/StockIndicator/StockIndicator";
import { useAddProductToWishlistFlow } from "../common/hooks/useAddProductToWishlistFlow";
import { type RootState } from "../redux/store";
import { selectProductDescriptor } from "../redux/products/productsSelectors";
import { Permission, userHasPermission } from "../utils/permissions";
import { removeFromComparison } from "./redux/actions";
import ProductDesignation from "../ProductDetails/components/ProductDesignation";
import { IconSize } from "../common/components/icon/IconSize";
import { OldDesignationParameter } from "../common/components/Product/ProductParams";
import { userIsDistributor, userIsOEM, userIsRegistered } from "../utils/roles";
import {
  getListPrice,
  getNetPrice,
  getRebatePercentage,
  getSurcharge,
  getTariff,
  isProductPMA,
} from "../feature/ProductDescriptor/v1/productDescriptorUtils";
import { selectAmountInCart } from "../AddToCart/redux/cartAmounts/cartAmountSelectors";
import { NewVersionMessage } from "../common/components/Messages/NewVersionMessage";
import CircleSpinner from "../common/components/CircleSpinner/CircleSpinner";
import { AddToCartService } from "../AddToCart/AddToCartService";
import { Translation } from "../common/Translation";
import { canSeeAddToCartButton, canSeeListPrice } from "../utils/features";
import { isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";
import { useTranslation } from "react-i18next";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { productHasAvailableCampaign } from "~/SalesCampaign/utils";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { Button } from "~/common/components/Button/Button";
import classes from "./ProductComparisonCard.module.less";
import classNames from "classnames";
import { SideButton } from "~/common/components/Button/SideButton";
import { selectProductIsInStock } from "~/utils/pna/availabilitySelectors";
import { isTariffSurchargeEnabled } from "~/Cart/cartUtils";

const WIDTHS = {
  product: "120px",
  gap: "10px",
  brand: "32px",
  manufacturer: "32px",
};

interface Props {
  productCode: string;
}

const ProductComparisonCard = ({ productCode }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const productDescriptor = useSelector((state: RootState) => {
    return selectProductDescriptor(state, {
      productCode,
      priceReference: getInitialPriceReference(),
    });
  });
  const qtyInCart = useSelector((state: RootState) =>
    selectAmountInCart(state, productCode, state.pdp.selectedPriceReference),
  );
  const selectPriceProps = { totalQuantity: qtyInCart, product: productDescriptor, packCode: "" };

  const isSpecial = isSpecialPrice(productDescriptor, "");
  const hasSurcharge = getSurcharge(selectPriceProps) > 0;

  const listPriceVisible = canSeeListPrice({
    product: productDescriptor,
    isSpecialPrice: isSpecial,
    hasSurcharge: hasSurcharge,
  });

  const tariffSurcharge = isTariffSurchargeEnabled() && getTariff(selectPriceProps.product);

  const netPrice = getNetPrice(selectPriceProps);
  const listPrice = listPriceVisible ? getListPrice({ product: productDescriptor, packCode: "" }) : null;
  const rebateValue = listPriceVisible ? getRebatePercentage(selectPriceProps) : null;

  const addToWishlistAvailable = userHasPermission(Permission.ADD_TO_WISHLIST);

  const handleCloseClick = () => {
    dispatch(removeFromComparison(productCode, productDescriptor.base.designation));
  };

  const doAddToCart = () => {
    AddToCartService.openAddToCartWizard({
      productCode: productDescriptor.base.code,
      restrictionQuery: "",
    });
  };

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({ productInput: productDescriptor.base });
  const onWishlistBtnClick = () => {
    openAddToWishlist();
  };

  const isInStock = selectProductIsInStock(productDescriptor.availability);

  const disableWS = window.SCHA.config.disableAllWS;

  const hasSales = productHasAvailableCampaign(productDescriptor.base.salesCampaign);

  return (
    <>
      {addToWishlistAvailable && addToWishlistModal}
      <div className="scha__comparison-card">
        {hasSales && <SaleTag className="scha__comparison-card__price-tag" size={40} />}
        <div className="product-image">
          <ProductImage product={productDescriptor.base} widths={WIDTHS} />
        </div>
        <ProductDesignation
          className={"mt-3"}
          isPreferred={productDescriptor.base.preferred}
          designation={productDescriptor.base.designation}
          size={"title-medium"}
          iconSize={IconSize.FONT_16}
        />
        <div className="title-wrapper ">
          <div className="title">
            <Translation {...productDescriptor.base.shortDescription} />
          </div>
          <div className="description">{productDescriptor.base.description} </div>
        </div>

        <NewVersionMessage className={"scha__comparison-card__new-version-msg"} baseProduct={productDescriptor.base} />

        <div className="old-designation-wrapper">
          <OldDesignationParameter label={t("productDetailPage.oldDesignation")} product={productDescriptor.base} />
        </div>

        <div className="actions-wrapper">
          {!disableWS && userHasPermission(Permission.PRICE) && (
            <Price
              variant={PriceType.ProductNetPriceFullLabel}
              price={netPrice}
              isSubscription={netPrice?.isSubscription ?? false}
              isSpecialPrice={isSpecial}
              includesSurcharge={hasSurcharge}
              unit={netPrice?.unit ?? 1}
              rebateValue={rebateValue}
              tariffSurcharge={tariffSurcharge}
              rebateVariant={RebateLabelType.Default}
              classes={{
                label: "text-label",
                price: "scha__comparison-page__card__price",
                root: "scha__comparison-page__card__price__root",
              }}
            />
          )}
          {!disableWS && listPriceVisible && listPrice && (
            <Price
              variant={PriceType.ProductListPrice}
              price={listPrice}
              classes={{
                label: "text-label",
                price: "scha__comparison-page__card__price",
                root: "scha__comparison-page__card__price__root",
              }}
              unit={1}
            />
          )}
          <div className="actions-wrapper">
            {userHasPermission(Permission.AVAILABILITY) && productDescriptor.base.quantityVisible && (
              <>
                {productDescriptor.availability && (
                  <div className="stock" onClick={doAddToCart}>
                    <SimpleStockIndicator inStock={isInStock} checkAvailability />
                  </div>
                )}
                {productDescriptor.isLoading && <CircleSpinner className={"mt-3"} />}
              </>
            )}
            <div className="actions">
              {userIsDistributor() || userIsOEM() || canSeeAddToCartButton() ? (
                <>
                  <SideButton
                    size={"small"}
                    icon={IconType.Bookmark}
                    color={"secondary"}
                    onClick={onWishlistBtnClick}
                    className={classNames("scha__comparison-page__add-to-wishlist", classes.button, classes.wishlist)}
                  />
                  {isProductPMA(productDescriptor) ? (
                    <div className="scha__comparison-card__on-request-only" onClick={doAddToCart}>
                      <Icon type={IconType.InformationCircle} size={IconSize.FONT_24} className={"icon-margin"} />
                      <span>{t("product.pma.messages.short")}</span>
                    </div>
                  ) : (
                    <Button
                      size={"medium"}
                      startIcon={<Icon type={IconType.Cart} />}
                      disabled={disableWS}
                      onClick={doAddToCart}
                      fullWidth
                      className={classNames("p-0", classes.button)}
                    >
                      {userIsRegistered() ? t("addToCartPopup.add.submitBtn") : t("basket.addToBasket")}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  startIcon={<Icon type={IconType.Bookmark} />}
                  onClick={onWishlistBtnClick}
                  fullWidth
                  className={classNames(classes.button, classes.wishlist)}
                >
                  {t("page.search.tabular.action.addToWishlist")}
                </Button>
              )}
            </div>
          </div>
          <IconButton className="close-icon" color={"primary"} onClick={handleCloseClick}>
            <Icon type={IconType.Close} />
          </IconButton>
        </div>
      </div>
    </>
  );
};

export default ProductComparisonCard;
